import {
  collection,
  getDocs,
  doc,
  query,
  orderBy,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { fetchTransactions, fetchExpenses, fetchInvoices } from './financeService';
import { fetchAccountReceivables } from './accountReceivableService';
import { FinanceTransaction, Expense, Invoice } from './financeService';
import { AccountReceivable } from './accountReceivableService';

export interface AccountReceivableBreakdown {
  opening: number;
  totalReceivables: number;
  received: number;
  closing: number;
}

export interface AccountPayableBreakdown {
  opening: number;
  totalPayables: number;
  paid: number;
  closing: number;
}

export interface ProfitLossData {
  period: string;
  startDate: Date;
  endDate: Date;
  openingBalance: number;
  revenue: number;
  expenses: number;
  accountReceivable: AccountReceivableBreakdown;
  accountPayable: AccountPayableBreakdown;
  closingBalance: number;
  netProfit: number;
}

/**
 * Get opening balance (balance at the start of the period)
 */
const getOpeningBalance = async (
  companyId: string,
  startDate: Date
): Promise<number> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const transactionsRef = collection(financeDocRef, 'transactions');
  
  // Get all transactions before the start date, ordered by date
  const q = query(
    transactionsRef,
    where('date', '<', Timestamp.fromDate(startDate)),
    orderBy('date', 'desc')
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return 0;
  }
  
  // Get the last transaction before the period (by date)
  // If multiple transactions on the same date, get the one with the latest balance
  const transactions = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
      balance: data.balance || 0,
      createdAt: data.createdAt,
    };
  }).sort((a, b) => {
    // Sort by date descending, then by createdAt descending if dates are equal
    const dateDiff = b.date.getTime() - a.date.getTime();
    if (dateDiff !== 0) return dateDiff;
    const aCreated = a.createdAt instanceof Date ? a.createdAt : (a.createdAt as any)?.toDate ? (a.createdAt as any).toDate() : new Date(0);
    const bCreated = b.createdAt instanceof Date ? b.createdAt : (b.createdAt as any)?.toDate ? (b.createdAt as any).toDate() : new Date(0);
    return bCreated.getTime() - aCreated.getTime();
  });
  
  return transactions[0]?.balance || 0;
};

/**
 * Calculate profit and loss for a given period
 */
export const calculateProfitLoss = async (
  companyId: string,
  periodType: 'month' | 'half-year' | 'full-year',
  selectedMonth?: number, // 0-11 for month selection
  selectedYear?: number
): Promise<ProfitLossData> => {
  const now = new Date();
  const year = selectedYear || now.getFullYear();
  const month = selectedMonth !== undefined ? selectedMonth : now.getMonth();
  
  let startDate: Date;
  let endDate: Date;
  let periodLabel: string;
  
  switch (periodType) {
    case 'month':
      startDate = new Date(year, month, 1);
      endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                         'July', 'August', 'September', 'October', 'November', 'December'];
      periodLabel = `${monthNames[month]} ${year}`;
      break;
      
    case 'half-year':
      // First half: Jan-Jun, Second half: Jul-Dec
      const isFirstHalf = month < 6;
      startDate = new Date(year, isFirstHalf ? 0 : 6, 1);
      endDate = new Date(year, isFirstHalf ? 5 : 11, 31, 23, 59, 59, 999);
      periodLabel = isFirstHalf ? `H1 ${year}` : `H2 ${year}`;
      break;
      
    case 'full-year':
      startDate = new Date(year, 0, 1);
      endDate = new Date(year, 11, 31, 23, 59, 59, 999);
      periodLabel = `Year ${year}`;
      break;
      
    default:
      startDate = new Date(year, month, 1);
      endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);
      periodLabel = `${month + 1}/${year}`;
  }
  
  // Get opening balance
  const openingBalance = await getOpeningBalance(companyId, startDate);
  
  // Get all transactions in the period
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const transactionsRef = collection(financeDocRef, 'transactions');
  const transactionsQuery = query(
    transactionsRef,
    where('date', '>=', Timestamp.fromDate(startDate)),
    where('date', '<=', Timestamp.fromDate(endDate)),
    orderBy('date', 'asc')
  );
  
  const transactionsSnapshot = await getDocs(transactionsQuery);
  const transactions = transactionsSnapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
    } as FinanceTransaction;
  });
  
  // Calculate revenue (debit from invoices - money received)
  const revenueTransactions = transactions.filter(t => t.type === 'invoice');
  const revenue = revenueTransactions.reduce((sum, t) => sum + (t.debit || 0), 0);
  
  // Calculate expenses (credit from expenses - money paid out)
  const expenseTransactions = transactions.filter(t => t.type === 'expense');
  const expenses = expenseTransactions.reduce((sum, t) => sum + (t.credit || 0), 0);
  
  // Calculate Account Receivable breakdown
  const receivables = await fetchAccountReceivables(companyId);
  
  // Helper function to convert date
  const getReceivableDate = (r: AccountReceivable): Date => {
    if (r.date instanceof Date) {
      return r.date;
    } else if (r.date && typeof (r.date as any).toDate === 'function') {
      return (r.date as any).toDate();
    } else if (r.date && typeof r.date === 'object' && (r.date as any).seconds) {
      return new Date((r.date as any).seconds * 1000);
    } else {
      return new Date(r.date as any);
    }
  };
  
  const getPaidDate = (r: AccountReceivable): Date | null => {
    if (!r.paidDate) return null;
    if (r.paidDate instanceof Date) {
      return r.paidDate;
    } else if (r.paidDate && typeof (r.paidDate as any).toDate === 'function') {
      return (r.paidDate as any).toDate();
    } else if (r.paidDate && typeof r.paidDate === 'object' && (r.paidDate as any).seconds) {
      return new Date((r.paidDate as any).seconds * 1000);
    } else {
      return new Date(r.paidDate as any);
    }
  };
  
  // Opening AR: Receivables created before startDate that weren't fully paid
  const openingAR = receivables
    .filter(r => {
      const receivableDate = getReceivableDate(r);
      return receivableDate < startDate && r.status !== 'paid';
    })
    .reduce((sum, r) => {
      // Calculate what was remaining at start of period
      // If it was created before period and paid during period, we need to track the opening amount
      const paidDate = getPaidDate(r);
      if (paidDate && paidDate >= startDate) {
        // It was paid during the period, so opening was the total amount
        return sum + (r.totalAmount || 0);
      }
      return sum + (r.remainingAmount || 0);
    }, 0);
  
  // Total Receivables: New receivables created during the period
  const totalReceivables = receivables
    .filter(r => {
      const receivableDate = getReceivableDate(r);
      return receivableDate >= startDate && receivableDate <= endDate;
    })
    .reduce((sum, r) => sum + (r.totalAmount || 0), 0);
  
  // Received: Payments received during the period
  // Look at transactions to find payments received for receivables
  // Any invoice transaction with debit > 0 that matches a receivable invoice number
  // and was created during the period is a payment received
  const receivableInvoiceNumbers = receivables.map(r => r.invoiceNumber);
  const received = transactions
    .filter(t => {
      // Must be an invoice transaction with money coming in
      if (t.type !== 'invoice' || !t.debit || t.debit <= 0) return false;
      
      // Check if transaction ref matches any receivable invoice number
      const matchesReceivable = t.ref && receivableInvoiceNumbers.includes(t.ref);
      
      return matchesReceivable;
    })
    .reduce((sum, t) => sum + (t.debit || 0), 0);
  
  // Closing AR: Receivables that exist at endDate (not fully paid)
  const closingAR = receivables
    .filter(r => {
      const receivableDate = getReceivableDate(r);
      return receivableDate <= endDate && r.status !== 'paid';
    })
    .reduce((sum, r) => sum + (r.remainingAmount || 0), 0);
  
  const accountReceivable: AccountReceivableBreakdown = {
    opening: openingAR,
    totalReceivables,
    received,
    closing: closingAR,
  };
  
  // Calculate Account Payable breakdown
  const allExpenses = await fetchExpenses(companyId);
  
  const getExpenseDate = (e: Expense): Date => {
    if (e.date instanceof Date) {
      return e.date;
    } else if (e.date && typeof (e.date as any).toDate === 'function') {
      return (e.date as any).toDate();
    } else if (e.date && typeof e.date === 'object' && (e.date as any).seconds) {
      return new Date((e.date as any).seconds * 1000);
    } else {
      return new Date(e.date as any);
    }
  };
  
  // Opening AP: Pending expenses created before startDate
  const openingAP = allExpenses
    .filter(e => {
      const expenseDate = getExpenseDate(e);
      return expenseDate < startDate && e.status === 'pending';
    })
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  
  // Total Payables: New pending expenses created during the period
  const totalPayables = allExpenses
    .filter(e => {
      const expenseDate = getExpenseDate(e);
      return expenseDate >= startDate && expenseDate <= endDate && e.status === 'pending';
    })
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  
  // Paid: This should be 0 for payables
  // When an expense is paid, it's no longer a payable (it becomes an expense)
  // Payables are only expenses with status 'pending'
  // So "Paid" for payables doesn't make sense - we should show 0
  const paid = 0;
  
  // Closing AP: Pending expenses at endDate
  const closingAP = allExpenses
    .filter(e => {
      const expenseDate = getExpenseDate(e);
      return expenseDate <= endDate && e.status === 'pending';
    })
    .reduce((sum, e) => sum + (e.amount || 0), 0);
  
  const accountPayable: AccountPayableBreakdown = {
    opening: openingAP,
    totalPayables,
    paid,
    closing: closingAP,
  };
  
  // Calculate closing balance
  // Closing balance = Opening balance + Revenue - Expenses
  const closingBalance = openingBalance + revenue - expenses;
  
  // Net profit = Revenue - Expenses
  const netProfit = revenue - expenses;
  
  return {
    period: periodLabel,
    startDate,
    endDate,
    openingBalance,
    revenue,
    expenses,
    accountReceivable,
    accountPayable,
    closingBalance,
    netProfit,
  };
};
